import { Injectable, signal } from '@angular/core'
import { TranslateService } from '@ngx-translate/core'
import { StorageService } from './storage.service'

@Injectable({
  providedIn: 'root',
})
export class PersonnalisationService {
  /**
   * Logo du programme, gardé en mémoire.
   *
   * Le header est reconstruit à chaque navigation. S'il devait lire le logo
   * dans le storage à chaque instanciation, il se rendrait d'abord sans logo
   * puis le ferait apparaître une centaine de millisecondes plus tard, ce qui
   * se voit pendant la transition de page. Le cache lui permet de se rendre
   * complet dès sa première frame.
   */
  readonly logo = signal<string | undefined>(undefined)

  constructor(private storage: StorageService) {}

  setLogo(logo: string | undefined): void {
    this.logo.set(logo)
  }

  /** Alimente le cache pour les parcours qui ne passent pas par le lancement. */
  async loadLogo(): Promise<void> {
    if (this.logo() !== undefined) {
      return
    }
    const program = await this.storage.get('program')
    this.logo.set(program?.config?.logo)
  }

  async setColors(): Promise<void> {
    const program = await this.storage.get('program')
    if (!program || !program.config.colors) {
      return
    }
    const htmlEl: HTMLElement | null = document.querySelector('html')
    if (htmlEl) {
      htmlEl.style.setProperty(
        '--ion-color-primary',
        program.config.colors.primary
      )
      htmlEl.style.setProperty(
        '--ion-color-primary-shade',
        this.adjust(program.config.colors.primary, -30)
      )
      htmlEl.style.setProperty(
        '--ion-color-primary-tint',
        this.adjust(program.config.colors.primary, 30)
      )
      htmlEl.style.setProperty(
        '--ion-color-secondary',
        program.config.colors.secondary
      )
      htmlEl.style.setProperty(
        '--ion-color-secondary-shade',
        this.adjust(program.config.colors.secondary, -30)
      )
      htmlEl.style.setProperty(
        '--ion-color-secondary-tint',
        this.adjust(program.config.colors.secondary, 30)
      )
    }
  }

  private adjust(color: string, amount: number): string {
    return (
      '#' +
      color
        .replace(/^#/, '')
        .replace(/../g, (color) =>
          (
            '0' +
            Math.min(255, Math.max(0, parseInt(color, 16) + amount)).toString(
              16
            )
          ).substr(-2)
        )
    )
  }

  async setTitle(): Promise<void> {
    const program = await this.storage.get('program')
    if (!program || !program.name) {
      return
    }
    document.title = program.name
  }
}
